<template>
  <div class="depot-card">
    <div class="depot-card-heading">
      <div class="depot-card-heading__main">
        <p class="title">
          <a class="consignment-link" :href="'/bookings/' + consignment.id">
            {{ consignment.number }}
          </a>
        </p>
        <span
          class="status"
          :class="[
            {
              't-bg-red':
                consignment.status === 'Booked' &&
                (convertPickupAfter(consignment.pickup_after) > currentDate ||
                  consignment.ready_for_pickup === false)
            },
            {
              't-bg-orange':
                consignment.status === 'Booked' &&
                (convertPickupAfter(consignment.pickup_after) <= currentDate ||
                  consignment.ready_for_pickup === true)
            },
            { 't-bg-dodger-blue': consignment.status === 'In Transit' },
            { 't-bg-gray-500': consignment.status === 'Cancelled' },
            { 't-bg-forest-green': consignment.status === 'Delivered' }
          ]"
        >
          {{ consignment.status }}
        </span>
      </div>
      <div class="depot-card-heading__side">
        <span>
          <p class="depot-charge">
            <span class="t-font-bold">SENDER:</span> {{ consignment.sender }}
          </p>
        </span>
      </div>
      <div class="depot-card-heading__side">
        <span>
          <p class="depot-charge" v-if="consignment.depot_charge">
            ${{ consignment.depot_charge }}
          </p>
        </span>
      </div>
    </div>
    <div class="depot-card-meta">
      <ul class="depot-card-meta__info">
        <li>
          <p class="title">CARRIER</p>
          <span>
            {{ consignment.carrier }}
          </span>
        </li>
      </ul>
      <ul class="depot-card-meta__info">
        <div class="md:t-col-span-6 md:t-px-4">
          <p
            class="
              t-text-gray-800
              t-tracking-wide
              t-uppercase
              t-text-sm
              t-font-semibold
            "
          >
            <b>Pickup Ref:</b> {{ separateRef(consignment.pickup_ref) }}<br />
            <b>Delivery Reference:</b>
            {{ separateRef(consignment.delivery_ref) }}
          </p>
        </div>
      </ul>
    </div>
    <div class="depot-card-body">
      <div v-if="consignmentType === 'INCOMING'">
        <p
          class="
            t-mb-1 t-text-xs t-font-semibold t-uppercase t-text-dodger-blue
          "
        >
          From
        </p>
        <p class="t-font-semibold">{{ consignment.pickup_location.name }}</p>
        <p>
          {{ consignment.pickup_location.address.street_number }}
          {{ consignment.pickup_location.address.street_name }}
        </p>
        <p>{{ consignment.pickup_location.address.suburb }}</p>
        <p>{{ consignment.pickup_location.address.city }}</p>

        <div class="t-mt-3">
          <p>
            After
            {{ moment(consignment.pickup_after).format("hh:mm A ddd DD MMM") }}
          </p>
          <p v-if="consignment.pickup_before">
            Before
            {{ moment(consignment.pickup_before).format("hh:mm A ddd DD MMM") }}
          </p>
        </div>
      </div>
      <div v-if="consignmentType === 'OUTGOING'">
        <p
          class="
            t-mb-1 t-text-xs t-font-semibold t-uppercase t-text-dodger-blue
          "
        >
          To
        </p>
        <p class="t-font-semibold">{{ consignment.delivery_location.name }}</p>
        <p>
          {{ consignment.delivery_location.address.street_number }}
          {{ consignment.delivery_location.address.street_name }}
        </p>
        <p>{{ consignment.delivery_location.address.suburb }}</p>
        <p>{{ consignment.delivery_location.address.city }}</p>

        <div class="t-mt-3">
          <p>
            Before
            {{
              moment(consignment.deliver_before).format("hh:mm A ddd DD MMM")
            }}
          </p>
        </div>
      </div>
      <div class="freight t-flex t-flex-col">
        <div
          class="
            t-grid
            t-grid-cols-10
          "
        >
          <p
            class="t-text-xs t-font-semibold t-uppercase t-text-dodger-blue t-col-span-7 t-pr-2"
          >
            Freight
          </p>
          <p
            class="t-text-xs t-font-semibold t-uppercase t-text-dodger-blue t-col-span-1"
          >
            Weight
          </p>
          <p
            class="t-text-xs t-font-semibold t-uppercase t-text-dodger-blue t-col-span-2 t-text-right"
          >
            Volume
          </p>
        </div>
        <div
          class="
            t-grid
            t-grid-cols-10
            t-border-t-2
            t-border-b-2
            t-border-gray-300
            t-py-2
          "
        >
          <template v-for="freight in consignment.lines">
            <p class="t-col-span-7 t-pr-2">
              {{ freight.quantity }} x {{ freight.package_type.name }}
              {{ freight.goods_type.name }} [{{ freight.description }}]
            </p>
            <p class="t-col-span-1">{{ parseFloat(freight.weight) }}kg</p>
            <p class="t-col-span-2 t-text-right">
              {{ parseFloat(freight.volume) }}m<sup>3</sup>
            </p>
          </template>
          <p v-if="consignment.contains_dgs" class="t-col-span-10">
            <strong>DG Classes</strong>: {{ consignment.dg_classes }}
          </p>
        </div>
        <div
          class="
            t-grid
            t-grid-cols-10
            t-border-b-2
            t-border-gray-300
            t-py-2
            t-font-semibold
            t-text-gray-800
          "
        >
          <p class="t-col-span-7">TOTAL</p>
          <p class="t-col-span-1">
            {{ parseFloat(getTotalWeight(consignment.lines)) }}kg
          </p>
          <p class="t-col-span-2 t-text-right">
            {{ parseFloat(getTotalVolume(consignment.lines)) }}m<sup>3</sup>
          </p>
        </div>
      </div>

      <!-- start of bin locations -->
      <div class="t-pl-10 t-col-span-2">
        <table class="t-w-full">
          <tr>
            <th
              class="
                t-border-b-2
                t-border-gray-300
                t-mb-1 
                t-text-xs 
                t-font-semibold 
                t-uppercase 
                t-text-dodger-blue
                t-w-1/2
              "
            >
              Booking
            </th>
            <th
              class="
                t-border-b-2
                t-border-gray-300
                t-mb-1 
                t-text-xs 
                t-font-semibold 
                t-uppercase 
                t-text-dodger-blue
                t-w-1/2
              "
            >
              Bin Location
            </th>
          </tr>
          <tr v-for="booking in consignment.bookings" :key="booking.id">
            <td class="t-w-1/2 t-py-2 t-align-top">{{ booking.number }}</td>
            <td class="t-w-1/2 t-py-2 t-align-top">
              <span v-if="booking.bin_locations.length > 0">
                <span
                  v-for="(bin_location, idx) in booking.bin_locations"
                  :key="bin_location.id"
                >
                  {{ bin_location.name }}
                  <span v-if="idx != booking.bin_locations.length - 1">,</span>
                </span>
              </span>
              <span v-else>
                -
              </span>
            </td>
          </tr>
        </table>
      </div>
      <!-- end of bin locations -->
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "DepotCard",
  props: {
    consignment: {
      type: Object,
      required: true
    },
    consignmentType: {
      required: true
    }
  },
  data() {
    return {
      currentDate: moment().format("YYYY-MM-DD HH:mm")
    };
  },
  methods: {
    convertPickupAfter(pickupAfter) {
      return moment(pickupAfter).format("YYYY-MM-DD HH:mm");
    },

    getTotalWeight(freightLines) {
      return freightLines
        .reduce((totalWeight, freightLine) => {
          return totalWeight + parseFloat(freightLine.weight);
        }, 0.0)
        .toFixed(2);
    },
    getTotalVolume(freightLines) {
      return freightLines
        .reduce((totalVolume, freightLine) => {
          return totalVolume + parseFloat(freightLine.volume);
        }, 0.0)
        .toFixed(2);
    },
    moment(...args) {
      return moment(...args);
    },
    separateRef(ref) {
      if (Array.isArray(ref)) {
        return ref.join();
      } else {
        return ref;
      }
    }
  }
};
</script>

<style scoped>
.consignment-link:hover {
  color: #16a8e2;
}

.t-bg-red {
  background: #ff0000;
  border-color: #ff0000;
}
.depot-card-meta {
  justify-content: left;
}
</style>
